import "./App.css";
import { Breadcrumb, Card, Col, Divider, Flex, Layout, Menu, Row, Skeleton, theme } from "antd";
import Sider from "antd/es/layout/Sider";
import { Content, Header } from "antd/es/layout/layout";
import {
  Link,
  Navigate,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router";
import { createContext, useContext, useEffect, useState } from "react";
import {
  DashboardOutlined, ExportOutlined,
  SafetyOutlined,
  SettingOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import logo from "./logo.svg";
import UserAvatar from "./common/UserAvatar";
import { UserInfoContext } from "./FetchUserInfo";
import { useMatches } from "react-router";
import Breadcumbs from "./common/Breadcrumbs";
import {
  Certificate,
  ClipboardClock,
  Drill,
  FileBox,
  FileText,
  Ghost,
  HardHat,
  Network,
  ScanHeart,
  ScanText,
  ShieldUser,
  Users,
  Van,
} from "lucide-react";

export const UserContext = createContext({});

function App() {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useContext(UserInfoContext);
  const { token } = theme.useToken();

  const items = [
    { label: "Dashboard", key: "/dashboard", icon: <DashboardOutlined /> },
    { label: "Dipendenti", key: "/employees", icon: <Users size={16} /> },
    {
      label: "DPI",
      type: "group",
      children: [
        {
          label: "Prodotti",
          icon: <HardHat size={16}/>,
          permissions: ["dpi.read"],
          key: "/dpiProducts",
        },
        {
          label: "Seriali",
          icon: <ScanText size={16}/>,
          permissions: ["dpi.read"],
          key: "/dpiInventoryUnits",
        },
        {
          label: "Movimenti",
          icon: <SwapOutlined/>,
          permissions: ["dpi.read"],
          key: "/dpiTransactions",
        },
      ],
    },
    {
      label: "Documenti",
      type: "group",
      children: [
        {
          label: "Documenti aziendali",
          icon: <FileText  size={16} />,
          key: "/orgDocuments",
        },
        {
          label: "Corsi",
          icon: <Certificate size={16} />,
          permissions: ["dpi.read"],
          key: "/courses",
        },
        {
          label: "Nomine",
          icon: <ShieldUser size={16} />,
          permissions: ["dpi.read"],
          key: "/appointments",
        },
        {
          label: "Visite mediche",
          icon: <ScanHeart size={16}/>,
          permissions: ["dpi.read"],
          key: "/medicalVisits",
        },
      ],
    },
    {
      label: "Asset",
      type: "group",
      children: [
        {
          label: "Veicoli",
          icon: <Van size={16} />,
          permissions: ["dpi.read"],
          key: "/vehicles",
        },
        {
          label: "Attrezzature",
          icon: <Drill size={16} />,
          permissions: ["dpi.read"],
          key: "/equipments",
        },
      ],
    },

    {
      label: "Programmazione e controllo",
      type: "group",
      children: [
        {
          label: "Assenze",
          icon: <Ghost size={16} />,
          permissions: ["dpi.read"],
          key: "/absences",
        },
        {
          label: "Cantieri",
          icon: <Network size={16} />,
          permissions: ["dpi.read"],
          key: "/workSites",
        },
        {
          label: "Modelli POS",
          icon: <FileBox size={16} />,
          permissions: ["dpi.read"],
          key: "/posModels",
        },
        {
          label: "Programmazione",
          icon: <ClipboardClock size={16}/>,
          permissions: ["dpi.read"],
          key: "/resourceAllocation",
        },
      ],
    },
    {
      label: "Export",
      icon: <ExportOutlined/>,
      key: "/export"
    },
    {
      label: "Archivi",
      icon: <SettingOutlined />,
      children: [
        { label: "Tag", key: "/tags", icon: <SettingOutlined /> },
        {
          label: "Dpi",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/dpis",
        },
        {
          label: "Categorie corsi",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/courseTypes",
        },
        {
          label: "Categorie nomine",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/appointmentTypes",
        },
        {
          label: "Medici competenti",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/doctors",
        },
        {
          label: "Categorie veicoli",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/vehicleTypes",
        },
        {
          label: "Categorie allestimenti",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/setupTypes",
        },
        {
          label: "Categorie attrezzature",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/equipmentTypes",
        },
        {
          label: "Categorie assenze",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/absenceTypes",
        },
        {
          label: "Categorie attività",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/workActivities",
        },
        {
          label: "Mansioni",
          icon: <SettingOutlined />,
          permissions: ["dpi.read"],
          key: "/mansions",
        },
      ],
    },
  ];

  const filterMenu = (menu) => {
    if (Array.isArray(menu)) return menu.map((x) => filterMenu(x)).filter((x) => !!x);

    const hasPermission = (menu.permissions ?? []).every(
      (y) => userInfo.permissions?.includes(y) ?? false,
    );
    if (!hasPermission) return null;
    if (!!menu.children) {
      const children = filterMenu(menu.children);
      if (children.length === 0) return null;
      else return { ...menu, children };
    }
    return menu;
  };

  const filteredItems = filterMenu(items);
  const defaultItem = (items) => {
    for (const item of items) {
      if (item.key) return item.key;
      const res = defaultItem(item.children);
      if (res) return res;
    }
    return null;
  };

  if (location.pathname === "/" && items.length > 0)
    return <Navigate to={defaultItem(filteredItems)} replace={true} />;

  return (
    <Layout>
      <Sider
        width={250}
        style={{
          overflowY: "auto",
          position: "sticky",
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          scrollbarWidth: "thin",
          scrollbarGutter: "stable",
          height: "100vh",
          borderRight: "1px solid " + token.colorBorder,
          //background: token.colorBgLayout,
          background: "#fff",
        }}
      >
        <img alt="logo" style={{ width: 40, flexShrink: 0 }} src={logo} />

        <div style={{ flexShrink: 0 }}>
          <UserAvatar user={{ firstName: "Nikola", lastName: "Marku" }} />
        </div>
        <Menu
          theme="light"
          style={{ /*background: token.colorBgLayout*/ background: "#fff" }}
          mode="vertical"
          onSelect={({ key }) => navigate(key)}
          items={filteredItems}
          selectedKeys={["/" + location.pathname.split("/")[1]]}
        />
      </Sider>
      <Layout style={{ overflowY: "scroll", height: "100vh" }}>
        <Content
          style={{
            flex: 1,
            background: "#fff",
            minHeight: "calc(100vh)",
          }}
        >
          <div style={{ padding: "32px 24px", background: "#fff" }}>
            <Flex vertical gap={32} style={{ paddingInline: 32 }}>
              {/*<Skeleton
                title={null}
                paragraph={{ rows: 1 }}
                style={{ width: 100 }}
                active
                loading={navigation.state === "loading"}
              >
              </Skeleton>*/}
              <Breadcumbs />
              {/*<Divider orientation="horizontal" />*/}
              <Skeleton active loading={navigation.state === "loading"}>
                <Outlet />
              </Skeleton>
            </Flex>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}

export default App;
